import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { TrendingUp, PieChart, Target } from "lucide-react";
import { PrimaryCTA, ExpertCTA } from "@/components/ui-kit";

const HEADLINE = ["Build Wealth With a", "Smarter Financial Plan."];

const lineVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const wordLine = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const GrowthCurve = () => (
  <svg viewBox="0 0 400 200" className="h-full w-full" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="curveFill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#F4B400" stopOpacity="0.28" />
        <stop offset="100%" stopColor="#F4B400" stopOpacity="0" />
      </linearGradient>
    </defs>
    {[40, 80, 120, 160].map((y) => (
      <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="#0A2342" strokeOpacity="0.06" strokeWidth="1" />
    ))}
    <motion.path
      d="M0 165 C 70 150, 110 120, 170 110 C 230 100, 270 60, 400 25"
      stroke="#F4B400"
      strokeWidth="3"
      strokeLinecap="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 2, ease: "easeOut", delay: 0.6 }}
    />
    <motion.path
      d="M0 165 C 70 150, 110 120, 170 110 C 230 100, 270 60, 400 25 L 400 200 L 0 200 Z"
      fill="url(#curveFill)"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.2, delay: 1.8 }}
    />
    <motion.circle
      cx="400" cy="25" r="5" fill="#F4B400"
      initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 2.4, type: "spring" }}
    />
  </svg>
);

export const KineticHero = () => {
  const { scrollYProgress } = useScroll();
  const blobY = useTransform(scrollYProgress, [0, 0.3], [0, 80]);

  return (
    <section id="top" className="relative overflow-hidden bg-lightbg pt-28 sm:pt-32 lg:pt-40">
      {/* parallax geometry */}
      <motion.div
        style={{ y: blobY }}
        className="pointer-events-none absolute -right-40 -top-24 h-[520px] w-[520px] rounded-full bg-royal/[0.07] blur-3xl"
      />
      <motion.div
        style={{ y: blobY }}
        className="pointer-events-none absolute -left-32 top-40 h-[360px] w-[360px] rounded-full bg-gold/[0.10] blur-3xl"
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 pb-16 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8 lg:pb-24">
        {/* copy */}
        <div>
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-navy/10 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-navy/70"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-gold" /> Personalised financial planning · India
          </motion.span>

          <h1 className="mt-6 font-display text-[2.6rem] font-extrabold leading-[1.03] tracking-tight text-navy sm:text-6xl lg:text-[4.25rem]">
            <motion.span variants={lineVariants} initial="hidden" animate="show" className="block">
              {HEADLINE.map((line, i) => (
                <span key={line} className="block overflow-hidden">
                  <motion.span variants={wordLine} className="block">
                    {i === 1 ? (
                      <>
                        Smarter <span className="text-royal">Financial Plan.</span>
                      </>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-ink/70 sm:text-lg"
          >
            From investing and mutual funds to insurance and tax planning, Auxieron helps you make
            smarter financial decisions with a plan built around your goals.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <PrimaryCTA testId="hero-primary-cta" />
            <ExpertCTA testId="hero-expert-cta" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-6 text-sm font-medium text-ink/50"
          >
            Personalised guidance <span className="text-gold">•</span> Multiple financial solutions{" "}
            <span className="text-gold">•</span> Built around your goals
          </motion.p>
        </div>

        {/* dashboard visual */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="rounded-3xl border border-navy/10 bg-white p-5 shadow-hover sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-ink/40">Goal Progress</p>
                <p className="mt-1 font-display text-lg font-bold text-navy">Your Financial Plan</p>
              </div>
              <span className="inline-flex items-center gap-1 rounded-full bg-royal/10 px-3 py-1 text-xs font-semibold text-royal">
                <TrendingUp className="h-3.5 w-3.5" /> On track
              </span>
            </div>

            <div className="mt-4 h-40 rounded-2xl bg-lightbg/70 p-3">
              <GrowthCurve />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-navy/[0.06] bg-lightbg/60 p-4">
                <div className="flex items-center gap-2 text-navy">
                  <PieChart className="h-4 w-4 text-royal" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-ink/50">Allocation</span>
                </div>
                <div className="mt-3 flex gap-1.5">
                  <span className="h-2 flex-[5] rounded-full bg-navy" />
                  <span className="h-2 flex-[3] rounded-full bg-royal" />
                  <span className="h-2 flex-[2] rounded-full bg-gold" />
                </div>
                <div className="mt-2 flex justify-between text-[11px] font-medium text-ink/50">
                  <span>Equity</span><span>Debt</span><span>Alt</span>
                </div>
              </div>

              <div className="rounded-2xl border border-navy/[0.06] bg-lightbg/60 p-4">
                <div className="flex items-center gap-2 text-navy">
                  <Target className="h-4 w-4 text-gold" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-ink/50">Goals</span>
                </div>
                {[["Retirement", 68], ["Education", 42]].map(([label, w]) => (
                  <div key={label} className="mt-2.5">
                    <div className="mb-1 flex justify-between text-[11px] font-medium text-ink/60">
                      <span>{label}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-navy/10">
                      <motion.div
                        className="h-full rounded-full bg-royal"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${w}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, delay: 0.3 }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-3 text-center text-[10px] text-ink/35">Illustrative dashboard. Not indicative of returns.</p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="absolute -bottom-5 -left-5 hidden rounded-2xl border border-navy/10 bg-white px-4 py-3 shadow-hover sm:block"
          >
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/40">SIP · Monthly</p>
            <p className="font-display text-base font-bold text-navy">Consistent investing</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
